import { useRef, useState, useEffect } from "react";
import { Play, Pause, Download } from "lucide-react";
import { mediaUrl } from "@/lib/api";

function fmt(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function RichAudio({ cover, title, subtitle, path }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => setDur(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); } else { a.play(); setPlaying(true); }
  };
  const seek = (e) => {
    const a = ref.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * dur;
  };

  return (
    <div className="rounded-3xl bg-[#FFFFFF] border border-[rgba(61,50,56,0.1)] p-5 sm:p-6 shadow-sm" data-testid="rich-audio">
      <audio ref={ref} src={mediaUrl(path)} preload="metadata" />
      <div className="flex gap-4 sm:gap-5">
        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl overflow-hidden bg-[#F3E8E2] shrink-0">
          {cover ? <img src={mediaUrl(cover)} alt={title} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center font-serif text-4xl text-[rgba(198,137,158,0.5)]">♪</div>}
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
          <div className="min-w-0">
            {subtitle && <div className="eyebrow mb-1 truncate">{subtitle}</div>}
            <h4 className="font-serif text-xl sm:text-2xl text-[#3D3238] leading-tight truncate">{title || "Audio"}</h4>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button onClick={toggle} data-testid="rich-audio-toggle"
              className="w-12 h-12 shrink-0 rounded-full bg-[#C6899E] text-[#FBF5F1] flex items-center justify-center hover:bg-[#B4718C] transition-colors">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="h-1.5 bg-[#EFE3DC] rounded-full cursor-pointer" onClick={seek}>
                <div className="h-full bg-[#C6899E] rounded-full" style={{ width: `${dur ? (cur / dur) * 100 : 0}%` }} />
              </div>
              <div className="flex justify-between mt-1.5 text-[0.7rem] text-[#9C8F94] tabular-nums">
                <span>{fmt(cur)}</span><span>{fmt(dur)}</span>
              </div>
            </div>
            <a href={mediaUrl(path)} download target="_blank" rel="noreferrer" data-testid="rich-audio-download"
              className="w-10 h-10 shrink-0 rounded-full border border-[rgba(198,137,158,0.4)] flex items-center justify-center text-[#C6899E] hover:bg-[rgba(198,137,158,0.12)] transition-colors">
              <Download className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
